import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as CloseIcon } from '../../../../assets/icons/close-icon.svg';
import DefaultApiLimit from '../../../../config/defaultLimit';
import { ApiRequest } from '../../../../interface/common.interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  fetchCpoSessionFilters,
  fetchCpoSessions,
  setSortData,
  updateCPOSessionsFilters,
} from '../../../../redux/slices/OCPI/CPOSessionsSlice';
import { RootState } from '../../../../redux/store';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import ModalBox from '../../../../shared-components/modal-box/ModalBox';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import ConvertUTCtoDateTime from '../../../../shared-functions/ConvertUTCtoDateTime';

// ── Types ─────────────────────────────────────────────────────────────────────

interface OcpiSession {
  id: string;
  status?: string;
  kwh?: number;
  currency?: string;
  total_cost?: { excl_vat?: number; incl_vat?: number };
  start_date_time?: string;
  end_date_time?: string;
  location_id?: string;
  evse_uid?: string;
  connector_id?: string;
  party_id?: string;
  country_code?: string;
  auth_method?: string;
  tariff_id?: string;
  remark?: string;
  last_updated?: string;
  _objectId?: string;
  _lastUpdated?: string;
  _createdAt?: string;
  createdAt?: string;
  created_at?: string;
  created?: string;
}

// ── Sub-components ─────────────────────────────────────────────────────────────

/**
 * Renders a status badge for a session.
 * @param {any} props The props.
 * @param {any} props.status The status.
 */
function StatusBadge({ status }: Readonly<{ status: string }>) {
  let modifier = 'cpo-session-status-badge--pending';
  if (status === 'ACTIVE') {
    modifier = 'cpo-session-status-badge--active';
  } else if (status === 'COMPLETED') {
    modifier = 'cpo-session-status-badge--completed';
  } else if (status === 'INVALID') {
    modifier = 'cpo-session-status-badge--invalid';
  }

  return <span className={`cpo-session-status-badge ${modifier}`}>{status}</span>;
}

// ── Session Drill-Down Modal ─────────────────────────────────────────────────────

/**
 * Renders the session drill-down modal showing raw session detail.
 * @param {any} props The props.
 * @param {any} props.session The session details.
 * @param {any} props.onClose The close callback.
 */
function SessionDrillDownModal({
  session,
  onClose,
}: Readonly<{
  session: OcpiSession;
  onClose: () => void;
}>) {
  return (
    <ModalBox
      open
      header={false}
      notification
      title={null}
      closeModal={onClose}
      content={
        <div className="cpo-session-drilldown-modal">
          {/* Header */}
          <div className="cpo-drilldown-header">
            <div>
              <h3 className="cpo-drilldown-title">{session.id}</h3>
              <p className="cpo-drilldown-subtitle">
                {session.status ? <StatusBadge status={session.status} /> : '–'}
              </p>
            </div>
            <button type="button" onClick={onClose} className="cpo-drilldown-close">
              <CloseIcon width={30} height={30} />
            </button>
          </div>

          {/* Raw session detail */}
          <div className="cpo-drilldown-body">
            <pre className="cpo-session-drilldown-detail">{JSON.stringify(session, null, 2)}</pre>
          </div>
        </div>
      }
    />
  );
}

// ── Main Panel ────────────────────────────────────────────────────────────────

/**
 * CpoSessionsPanel component for viewing CPO OCPI charging sessions.
 * @param {any} props The props.
 * @param {any} props.countryCode The country code.
 * @param {any} props.partyId The party ID.
 */
export default function CpoSessionsPanel({
  countryCode,
  partyId,
}: Readonly<{
  countryCode: string;
  partyId: string;
}>) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation(['ocpi']);
  const {
    sessions,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    sortBy,
    sortField,
    filters,
    filterListLoading,
  } = useAppSelector((state: RootState) => state.cpoSessions);
  const [selectedSession, setSelectedSession] = useState<OcpiSession | null>(null);

  // ── Data fetching ──────────────────────────────────────────────────────────

  /**
   * Fetches CPO sessions for this role with the given pagination/sort parameters.
   * @param {any} data - The pagination/sort parameters.
   */
  const fetchData = useCallback(
    (data: ApiRequest) => {
      if (!loading) {
        dispatch(fetchCpoSessions({ countryCode, partyId, ...data }));
      }
    },
    [loading, dispatch, countryCode, partyId],
  );

  useEffect(() => {
    dispatch(fetchCpoSessions({ countryCode, partyId }));
  }, [countryCode, partyId, dispatch]);

  interface SessionRow {
    id: string;
    status: string;
    kwh: string;
    totalCost: string;
    partyId: string;
    countryCode: string;
    locationId: string;
    evseUid: string;
    connectorId: string;
    authMethod: string;
    tariffId: string;
    startDatetime: string;
    endDatetime: string;
    lastUpdated: string;
    _createdAt?: string;
    createdAt: string;
    raw: OcpiSession;
  }

  const columns = [
    {
      label: t('ocpi:created'),
      accessorKey: '_createdAt' as keyof SessionRow,
      sortAllow: false,
      /**
       * Rendering created date
       * @param {SessionRow} currentRow The current session row.
       * @returns {any} The rendered created date element.
       */
      customData: (currentRow: SessionRow) => (
        <span>{ConvertUTCtoDateTime(currentRow._createdAt || currentRow.createdAt)}</span>
      ),
    },
    {
      label: t('ocpi:sessionId'),
      accessorKey: 'id' as keyof SessionRow,
      sortAllow: false,
      columnWidth: 260,
      /**
       * Renders the session ID in a code badge.
       * @param {any} row The session row.
       */
      customData: (row: SessionRow) => <code className="cpo-session-id-badge">{row.id}</code>,
    },
    {
      label: t('ocpi:status'),
      accessorKey: 'status' as keyof SessionRow,
      sortAllow: false,
      /**
       * Renders the status badge.
       * @param {any} row The session row.
       */
      customData: (row: SessionRow) => <StatusBadge status={row.status} />,
    },
    {
      label: t('ocpi:energy'),
      accessorKey: 'kwh' as keyof SessionRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:totalCost'),
      accessorKey: 'totalCost' as keyof SessionRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:partyId'),
      accessorKey: 'partyId' as keyof SessionRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:countryCode'),
      accessorKey: 'countryCode' as keyof SessionRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:locationId'),
      accessorKey: 'locationId' as keyof SessionRow,
      sortAllow: false,
      /**
       * Renders the location ID in a code badge.
       * @param {any} row The session row.
       */
      customData: (row: SessionRow) => (
        <code className="cpo-session-id-badge">{String(row.locationId).slice(0, 18)}</code>
      ),
    },
    {
      label: t('ocpi:evseUid'),
      accessorKey: 'evseUid' as keyof SessionRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:connectorId'),
      accessorKey: 'connectorId' as keyof SessionRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:authMethod'),
      accessorKey: 'authMethod' as keyof SessionRow,
      sortAllow: false,
    },
    // {
    //   label: t('ocpi:tariffId'),
    //   accessorKey: 'tariffId' as keyof SessionRow,
    //   sortAllow: false,
    //   /**
    //    * Renders the tariff ID in a code badge.
    //    * @param {any} row The session row.
    //    */
    //   customData: (row: SessionRow) => (
    //     <code className="cpo-session-id-badge">{String(row.tariffId).slice(0, 18)}</code>
    //   ),
    // },
    {
      label: t('ocpi:started'),
      accessorKey: 'startDatetime' as keyof SessionRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:ended'),
      accessorKey: 'endDatetime' as keyof SessionRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:created'),
      accessorKey: 'createdAt' as keyof SessionRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:lastUpdated'),
      accessorKey: 'lastUpdated' as keyof SessionRow,
      sortAllow: false,
    },
  ];

  const tableData: SessionRow[] = sessions.map((session: OcpiSession) => {
    const sessionId = session.id || session._objectId || '';
    const dateString = session.last_updated || session._lastUpdated;
    const lastUpdated = dateString ? new Date(dateString).toLocaleDateString() : '–';
    const startDatetime = session.start_date_time
      ? new Date(session.start_date_time).toLocaleString()
      : '–';
    const endDatetime = session.end_date_time
      ? new Date(session.end_date_time).toLocaleString()
      : '–';
    const cost = session.total_cost?.incl_vat ?? session.total_cost?.excl_vat;
    const totalCost = cost !== undefined ? `${cost} ${session.currency || ''}`.trim() : '–';
    const createdAt =
      session._createdAt ||
      session.createdAt ||
      (session as any).created_at ||
      (session as any).created ||
      '';

    return {
      id: sessionId,
      status: session.status || t('ocpi:unknown'),
      kwh: session.kwh !== undefined ? `${session.kwh} kWh` : '–',
      totalCost,
      partyId: session.party_id || '–',
      countryCode: session.country_code || '–',
      locationId: session.location_id || '–',
      evseUid: session.evse_uid || '–',
      connectorId: session.connector_id || '–',
      authMethod: session.auth_method || '–',
      tariffId: session.tariff_id || '–',
      startDatetime,
      endDatetime,
      lastUpdated,
      _createdAt: createdAt,
      createdAt,
      raw: session,
    };
  });

  /**
   * Table pagination function
   * @param {any} value - The value
   */
  const pageAndLimitChange = (value: ApiRequest) => {
    fetchData({ ...value, sortField: sortField ?? undefined, sortBy: sortBy ?? undefined });
  };

  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: { sortField: string; sortBy: 'asc' | 'desc' }) => {
    dispatch(setSortData(sortData));
    fetchData({
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortField, sortBy });
  };

  return (
    <div className="cpo-sessions-panel">
      {/* ── Top bar ── */}
      <div className="cpo-sessions-topbar">
        <div>
          <h3 className="cpo-panel-title">{t('ocpi:ocpiSessions')}</h3>
          <p className="cpo-panel-subtitle">
            {sessions.length > 0
              ? `${sessions.length} ${t(
                  sessions.length !== 1 ? 'ocpi:sessionCountPlural' : 'ocpi:sessionCountSingular',
                )} ${t('ocpi:inOcpiGateway')}`
              : t('ocpi:sessionsEmptySubtitle')}
          </p>
        </div>
      </div>

      {/* ── Sessions Table ── */}
      <CustomTable
        version="v2"
        header={columns}
        data={tableData}
        limit={limit}
        page={page}
        totalPages={totalPages}
        totalResults={totalResults}
        loading={loading}
        serialNo
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={pageAndLimitChange}
        filters={filters}
        filtersToDispatch={updateCPOSessionsFilters}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchCpoSessionFilters}
        sizeChangeButtonRequired
        actionDropdown
        rowClickEvent={(_id, row) => setSelectedSession(row.raw)}
        rowClickParamsKey="id"
        refershCurrentPages={handleRefresh}
        requiredRefreshToggle
        refreshFunction={handleRefresh}
        refreshRequired
        handleRefresh={handleRefresh}
        hoverLabel={t('ocpi:refresh')}
      />

      {/* ── Drill-down modal ── */}
      {selectedSession && (
        <SessionDrillDownModal session={selectedSession} onClose={() => setSelectedSession(null)} />
      )}
    </div>
  );
}
